import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Badge,
  Stack,
  Text,
  Alert,
  Group,
  ScrollArea,
  Radio,
  Tooltip,
  Button
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconExclamationMark,
  IconInfoCircle,
  IconChevronDown,
  IconChevronUp
} from '@tabler/icons-react';

import { RESOURCE_TYPE_LABELS, CONFLICT_RESOLUTION } from '../import/importConstants';

/**
 * ImportPreviewModal - Shows detailed view of resources for a specific type
 */
const ImportPreviewModal = ({
  opened,
  onClose,
  resourceType,
  validationResults,
  parsedData,
  globalConflictResolution,
  perResourceConflicts,
  onConflictResolutionChange
}) => {
  // State for expanding/collapsing warnings list
  const [showAllWarnings, setShowAllWarnings] = useState(false);

  // Reset expanded state when resource type changes or modal closes
  useEffect(() => {
    setShowAllWarnings(false);
  }, [resourceType, opened]);

  if (!resourceType || !validationResults || !parsedData) {
    return null;
  }

  const result = validationResults[resourceType];
  const resources = parsedData[resourceType] || [];

  if (!result) {
    return null;
  }

  /**
   * Handle per-resource conflict resolution change
   */
  const handleResourceConflictChange = (resourceName, resolution) => {
    if (onConflictResolutionChange) {
      onConflictResolutionChange(resourceType, resourceName, resolution);
    }
  };

  /**
   * Get conflict resolution for a specific resource
   */
  const getResourceConflictResolution = (resourceName) => {
    if (perResourceConflicts && perResourceConflicts[resourceType] && perResourceConflicts[resourceType][resourceName]) {
      return perResourceConflicts[resourceType][resourceName];
    }
    return globalConflictResolution || CONFLICT_RESOLUTION.SKIP;
  };

  /**
   * Get status badge for a resource
   */
  const getResourceStatusBadge = (resourceName) => {
    const exists = result.conflicts && result.conflicts.includes(resourceName);

    if (exists) {
      return (
        <Badge color="yellow" leftSection={<IconExclamationMark size={12} />}>
          Exists
        </Badge>
      );
    }

    return (
      <Badge color="green" leftSection={<IconCheck size={12} />}>
        New
      </Badge>
    );
  };

  /**
   * Get dependency status for a resource
   */
  const getResourceDependencyStatus = (resourceName) => {
    const resourceDetail = result.resourceDetails?.find(r => r.name === resourceName);

    if (!resourceDetail || !resourceDetail.warnings || resourceDetail.warnings.length === 0) {
      return (
        <Badge color="green" variant="light">
          OK
        </Badge>
      );
    }

    return (
      <Badge color="orange" variant="light" leftSection={<IconAlertCircle size={12} />}>
        {resourceDetail.warnings.length} warnings
      </Badge>
    );
  };

  /**
   * Get warnings for a resource
   */
  const getResourceWarnings = (resourceName) => {
    const resourceDetail = result.resourceDetails?.find(r => r.name === resourceName);

    if (!resourceDetail || !resourceDetail.warnings || resourceDetail.warnings.length === 0) {
      return null;
    }

    return resourceDetail.warnings;
  };

  /**
   * Get unique warnings (deduplicated by message)
   */
  const getUniqueWarnings = (warnings) => {
    if (!warnings || warnings.length === 0) return [];
    const seen = new Set();
    return warnings.filter(warning => {
      if (seen.has(warning.message)) return false;
      seen.add(warning.message);
      return true;
    });
  };

  // Get unique warnings for display
  const uniqueWarnings = getUniqueWarnings(result.dependencyWarnings);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`${RESOURCE_TYPE_LABELS[resourceType]} - Detail View`}
      size="xl"
    >
      <Stack gap="md">
        {/* Summary */}
        <Alert icon={<IconCheck size={16} />} color="blue">
          <Group gap="xl">
            <Text size="sm">
              <strong>Total:</strong> {result.total}
            </Text>
            <Text size="sm" c="green">
              <strong>New:</strong> {result.new}
            </Text>
            <Text size="sm" c="yellow">
              <strong>Existing:</strong> {result.existing}
            </Text>
            {uniqueWarnings.length > 0 && (
              <Text size="sm" c="orange">
                <strong>Warnings:</strong> {uniqueWarnings.length}
              </Text>
            )}
          </Group>
        </Alert>

        {/* Resources Table */}
        <ScrollArea h={400}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Resource Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Dependencies</Table.Th>
                <Table.Th>Conflict Action</Table.Th>
                <Table.Th>Row</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {resources.map((resource, index) => {
                const warnings = getResourceWarnings(resource.name);
                const exists = result.conflicts && result.conflicts.includes(resource.name);
                const currentResolution = getResourceConflictResolution(resource.name);

                return (
                  <React.Fragment key={index}>
                    <Table.Tr>
                      <Table.Td>
                        <Text fw={500}>{resource.name}</Text>
                        {resource.description && (
                          <Text size="xs" c="dimmed">
                            {resource.description}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{getResourceStatusBadge(resource.name)}</Table.Td>
                      <Table.Td>{getResourceDependencyStatus(resource.name)}</Table.Td>
                      <Table.Td>
                        {exists ? (
                          <Group gap="xs">
                            <Radio.Group
                              value={currentResolution}
                              onChange={(value) => handleResourceConflictChange(resource.name, value)}
                              size="xs"
                            >
                              <Group gap="xs">
                                <Radio value={CONFLICT_RESOLUTION.SKIP} label="Skip" />
                                <Radio value={CONFLICT_RESOLUTION.UPDATE} label="Update" />
                              </Group>
                            </Radio.Group>
                            <Tooltip label="Per-resource override">
                              <IconInfoCircle size={14} color="gray" />
                            </Tooltip>
                          </Group>
                        ) : (
                          <Text size="xs" c="dimmed">N/A (New)</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light">{resource.rowNumber}</Badge>
                      </Table.Td>
                    </Table.Tr>

                    {/* Warnings Row */}
                    {warnings && warnings.length > 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Alert
                            icon={<IconAlertCircle size={14} />}
                            color="orange"
                            variant="light"
                            p="xs"
                          >
                            <Stack gap="xs">
                              {warnings.map((warning, wIndex) => (
                                <Text key={wIndex} size="xs">
                                  {warning.message}
                                </Text>
                              ))}
                            </Stack>
                          </Alert>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>

        {/* Global Warnings (deduplicated) */}
        {uniqueWarnings.length > 0 && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Dependency Warnings">
            <Stack gap="xs">
              {showAllWarnings ? (
                // Show all warnings in scrollable area
                <ScrollArea.Autosize mah={200}>
                  <Stack gap="xs">
                    {uniqueWarnings.map((warning, index) => (
                      <Text key={index} size="sm">
                        • {warning.message}
                      </Text>
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              ) : (
                // Show first 5 warnings
                <>
                  {uniqueWarnings.slice(0, 5).map((warning, index) => (
                    <Text key={index} size="sm">
                      • {warning.message}
                    </Text>
                  ))}
                </>
              )}
              {uniqueWarnings.length > 5 && (
                <Button
                  variant="subtle"
                  color="orange"
                  size="xs"
                  onClick={() => setShowAllWarnings(!showAllWarnings)}
                  leftSection={showAllWarnings ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                >
                  {showAllWarnings
                    ? 'Show less'
                    : `Show all ${uniqueWarnings.length} warnings`
                  }
                </Button>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>
    </Modal>
  );
};

export default ImportPreviewModal;
